import { UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, switchMap, take } from 'rxjs/operators';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { getRmsRecipeSpecifications } from '../selectors';
import { getAvailableAddedValues, getAvailableOpenPlatformAddedValues } from '../selectors/added-value.selectors';

export function hasPump(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableAddedValues).pipe(
        map((availables) => {
          const productCode = form.get(SpecFieldNames.PRODUCT_SELECTION).value;
          if (productCode) {
            return availables[productCode];
          } else {
            return [];
          }
        }),
        map((availables) => {
          if (availables && availables.every((av) => !av.loading)) {
            return availables;
          } else {
            return [];
          }
        }),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return true;
            } else {
              return technicals.some((technical) => technical.is_pumpable);
            }
          } else {
            return true;
          }
        }),
        take(1),
      );
    }),
  );
}
/**
 * Validate if the value added has technical product pumpable
 * but with the open plataform value added api (filter api)
 * @param store
 * @param form
 * @returns boolean
 */
export function hasPumpRMXUL(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableOpenPlatformAddedValues).pipe(
        map((availables) => {
          const contractControl = form.get(SpecFieldNames.CONTRACT_SELECT);
          const contractCode = contractControl ? contractControl.value : undefined;
          const formId = form.get(SpecFieldNames.ID).value;
          const key = `${contractCode}_${formId}`;
          if (contractCode) {
            return availables[key];
          } else {
            return [];
          }
        }),
        map((availables) => {
          if (availables && availables.every((av) => !av.loading)) {
            return availables;
          } else {
            return [];
          }
        }),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return true;
            } else {
              return technicals.some((technical) => technical.isPumpable);
            }
          } else {
            return true;
          }
        }),
        take(1),
      );
    }),
  );
}

export function hasDifferentToPump(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableAddedValues).pipe(
        map((availables) => {
          const productCode = form.get(SpecFieldNames.PRODUCT_SELECTION).value;
          if (productCode) {
            return availables[productCode];
          } else {
            return [];
          }
        }),
        map((availables) => {
          if (availables && availables.every((av) => !av.loading)) {
            return availables;
          } else {
            return [];
          }
        }),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return true;
            } else {
              return technicals.some((technical) => technical.is_pumpable === false);
            }
          } else {
            return true;
          }
        }),
        take(1),
      );
    }),
  );
}
/**
 * Validate if any technical product has different to pump (isPumpable = false)
 * but with the open plataform value added api (filter api)
 * @param store
 * @param form
 * @returns boolean
 */
export function hasDifferentToPumpRMXUL(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableOpenPlatformAddedValues).pipe(
        map((availables) => {
          const contractControl = form.get(SpecFieldNames.CONTRACT_SELECT);
          const contractCode = contractControl ? contractControl.value : undefined;
          const formId = form.get(SpecFieldNames.ID).value;
          const key = `${contractCode}_${formId}`;
          if (contractCode) {
            return availables[key];
          } else {
            return [];
          }
        }),
        map((availables) => {
          if (availables && availables.every((av) => !av.loading)) {
            return availables;
          } else {
            return [];
          }
        }),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return true;
            } else {
              return technicals.some((technical) => technical.isPumpable === false);
            }
          } else {
            return true;
          }
        }),
        take(1),
      );
    }),
  );
}

export function hasPumpAndHasDifferentToPump(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableAddedValues).pipe(
        map((availables) => {
          const productCode = form.get(SpecFieldNames.PRODUCT_SELECTION).value;
          return productCode ? availables[productCode] : [];
        }),
        map((availables) => (availables && availables.every((av) => !av.loading) ? availables : [])),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return { isPumpable: true, hasDifferentToPump: true };
            } else {
              return {
                isPumpable: technicals.some((technical) => technical.is_pumpable),
                hasDifferentToPump: technicals.some((technical) => technical.is_pumpable === false),
              };
            }
          } else {
            return { isPumpable: true, hasDifferentToPump: true };
          }
        }),
      );
    }),
  );
}

/**
 * Validate if any technical product is pumpable and has different to pump (isPumpable = false)
 * but with the open plataform value added api (filter api)
 * @param store
 * @param form
 * @returns {Observable<{isPumpable: boolean, hasDifferentToPump: boolean}>}
 */
export function hasPumpAndHasDifferentToPumpRMXUL(store: Store<any>, form: UntypedFormGroup) {
  return store.select(getRmsRecipeSpecifications).pipe(
    switchMap(() => {
      // Regular case
      return store.select(getAvailableOpenPlatformAddedValues).pipe(
        map((availables) => {
          const contractControl = form.get(SpecFieldNames.CONTRACT_SELECT);
          const contractCode = contractControl ? contractControl.value : undefined;
          const formId = form.get(SpecFieldNames.ID).value;
          const key = `${contractCode}_${formId}`;
          return contractCode ? availables[key] : [];
        }),
        map((availables) => (availables && availables.every((av) => !av.loading) ? availables : [])),
        map((availables) => availables[availables.length - 1]),
        map((last) => last && last.technicalProduct),
        map((technicals) => {
          if (technicals) {
            if (technicals.length === 0) {
              return { isPumpable: true, hasDifferentToPump: true };
            } else {
              return {
                isPumpable: technicals.some((technical) => technical.isPumpable),
                hasDifferentToPump: technicals.some((technical) => technical.isPumpable === false),
              };
            }
          } else {
            return { isPumpable: true, hasDifferentToPump: true };
          }
        }),
      );
    }),
  );
}
